import { normalizeUnit } from "./search";
import type { CtcaeTerm, GradeEvaluation, ReferenceKey, ReferenceValues, RuleBand } from "./types";

function inferredUnit(record: CtcaeTerm, value: number, supplied?: string) {
  const grading = record.numericGrading;
  if (!grading) return {};
  const normalized = normalizeUnit(supplied);
  if (normalized && grading.acceptedUnits.includes(normalized)) return { unit: normalized };
  if (normalized) return { choices: grading.acceptedUnits };
  if (grading.defaultUnit === "require-selection") return { choices: grading.acceptedUnits };
  if (grading.defaultUnit === "auto-platelets") {
    const unit = value >= 1000 ? "/mm3" : "x 10^9/L";
    return { unit, assumption: `Interpreting ${value.toLocaleString()} as ${unit}` };
  }
  const unit = grading.defaultUnit;
  return { unit, assumption: `Interpreting ${value.toLocaleString()} as ${unit}` };
}

function basisValue(rule: RuleBand, value: number, references: ReferenceValues) {
  const basis = rule.basis ?? "absolute";
  if (basis === "uln") return references.uln ? value / references.uln : undefined;
  if (basis === "baseline") return references.baseline ? value / references.baseline : undefined;
  if (basis === "delta-baseline") return references.baseline !== undefined ? value - references.baseline : undefined;
  return value;
}

function whenMatches(rule: RuleBand, references: ReferenceValues) {
  if (!rule.when) return true;
  if (rule.when === "baseline-at-or-below-uln") {
    return references.baseline !== undefined && references.uln !== undefined && references.baseline <= references.uln;
  }
  if (rule.when === "baseline-above-uln") {
    return references.baseline !== undefined && references.uln !== undefined && references.baseline > references.uln;
  }
  return references.baseline !== undefined && references.lln !== undefined && references.baseline < references.lln;
}

function matches(rule: RuleBand, value: number, unit: string, references: ReferenceValues) {
  if (rule.unit && rule.unit !== unit) return false;
  if (!whenMatches(rule, references)) return false;
  const comparison = basisValue(rule, value, references);
  if (comparison === undefined) return false;
  const max = rule.maxRef ? references[rule.maxRef] : rule.max;
  if (rule.min !== undefined && (rule.minInclusive ? comparison < rule.min : comparison <= rule.min)) return false;
  if (max !== undefined && (rule.maxInclusive ? comparison > max : comparison >= max)) return false;
  if (rule.maxRef && max === undefined) return false;
  return true;
}

function missingFor(rule: RuleBand, references: ReferenceValues) {
  const missing = new Set<ReferenceKey>();
  if (rule.basis === "uln" && !references.uln) missing.add("uln");
  if ((rule.basis === "baseline" || rule.basis === "delta-baseline") && references.baseline === undefined) missing.add("baseline");
  if (rule.maxRef && references[rule.maxRef] === undefined) missing.add(rule.maxRef);
  if (rule.when === "baseline-at-or-below-uln" || rule.when === "baseline-above-uln") {
    if (references.baseline === undefined) missing.add("baseline");
    if (references.uln === undefined) missing.add("uln");
  }
  if (rule.when === "baseline-below-lln") {
    if (references.baseline === undefined) missing.add("baseline");
    if (references.lln === undefined) missing.add("lln");
  }
  return [...missing];
}

function explanation(rule: RuleBand, value: number, unit: string, references: ReferenceValues) {
  if (rule.basis === "uln" && references.uln) return `${value} ÷ ${references.uln} = ${(value / references.uln).toFixed(2)} × ULN`;
  if (rule.basis === "baseline" && references.baseline) return `${value} ÷ ${references.baseline} = ${(value / references.baseline).toFixed(2)} × baseline`;
  if (rule.basis === "delta-baseline" && references.baseline !== undefined) return `${value} − ${references.baseline} = ${(value - references.baseline).toFixed(1)} ms from baseline`;
  if (rule.max !== undefined && rule.min === undefined) return `${value} ${unit} ${rule.maxInclusive ? "≤" : "<"} ${rule.max} ${unit}`;
  if (rule.min !== undefined && rule.max !== undefined) return `${rule.min} ${rule.minInclusive ? "≤" : "<"} ${value} ${rule.maxInclusive ? "≤" : "<"} ${rule.max} ${unit}`;
  if (rule.min !== undefined) return `${value} ${unit} ${rule.minInclusive ? "≥" : ">"} ${rule.min} ${unit}`;
  return undefined;
}

export function evaluateGrade(record: CtcaeTerm, value?: number, suppliedUnit?: string, references: ReferenceValues = {}): GradeEvaluation {
  const grading = record.numericGrading;
  if (value === undefined || !grading || grading.verificationStatus !== "verified") return {};
  const inferred = inferredUnit(record, value, suppliedUnit);
  if (!inferred.unit) return { unitChoices: inferred.choices };
  const unit = inferred.unit;

  const ambiguity = grading.ambiguities?.find((rule) => matches(rule, value, unit, references));
  if (ambiguity) {
    return {
      possibleGrades: ambiguity.grades,
      unit,
      assumption: inferred.assumption,
      message: ambiguity.message ?? "Additional clinical information is needed to determine the CTCAE grade.",
    };
  }

  const matching = grading.rules.filter((rule) => matches(rule, value, unit, references) && rule.grade);
  const selected = matching.sort((a, b) => (b.grade ?? 0) - (a.grade ?? 0))[0];
  if (selected?.grade) {
    return {
      grade: selected.grade,
      unit,
      assumption: inferred.assumption,
      explanation: explanation(selected, value, unit, references),
    };
  }

  const missing = new Set<ReferenceKey>();
  [...(grading.ambiguities ?? []), ...grading.rules]
    .filter((rule) => !rule.unit || rule.unit === unit)
    .forEach((rule) => missingFor(rule, references).forEach((key) => missing.add(key)));
  return {
    unit,
    assumption: inferred.assumption,
    missingReferences: [...missing],
    message: missing.size
      ? "Reference information is needed before a grade can be highlighted."
      : "The entered value alone does not determine a unique CTCAE grade.",
  };
}
